import { useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ClassTeachingGrid } from '@/features/institute/staff/components/ClassTeachingGrid';
import type { InstituteClass } from '@/types/instituteClass';

/**
 * The class's teaching grid in a dialog, from the classes table's row menu.
 * The grid itself is shared with the expanded row and the timetable.
 */

export interface SubjectTeachersModalProps {
  /** Never null: mounted only once a class is chosen, so each open reads fresh. */
  instituteClass: InstituteClass;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** Hands over to the class-teacher picker; omitted without `ALLOCATION_MANAGE`. */
  onAssignClassTeacher?: () => void;
  /** `ALLOCATION_MANAGE` — each subject's teacher can be picked here, cell by cell. */
  canEdit?: boolean;
}

export function SubjectTeachersModal({
  instituteClass,
  isOpen,
  onClose,
  onSaved,
  onAssignClassTeacher,
  canEdit = false,
}: SubjectTeachersModalProps) {
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveRef = useRef<(() => Promise<boolean>) | null>(null);

  // Matched by section name, as the class-teacher picker seeds itself — once
  // every section is covered the picker would have nothing left to offer.
  const hasUnassignedSection = instituteClass.sections.some(
    (section) =>
      !instituteClass.classTeachers.some((teacher) => teacher.sectionName === section.name),
  );

  async function handleSave() {
    if (!saveRef.current) return;
    const success = await saveRef.current();
    if (success) {
      onSaved?.();
      onClose();
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title={`Subject teachers — ${instituteClass.name}`}
      description="Class teacher and subject teachers assigned to each section."
      footer={
        <>
          {onAssignClassTeacher && hasUnassignedSection && (
            <Button variant="secondary" onClick={onAssignClassTeacher} disabled={isSaving}>
              Assign class teacher
            </Button>
          )}
          {!canEdit && (
            <Button
              variant="primary"
              onClick={onClose}
              disabled={isSaving}
            >
              Close
            </Button>
          )}
          {canEdit && (
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              isLoading={isSaving}
              disabled={isSaving || !hasChanges}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      <ClassTeachingGrid
        classUuid={instituteClass.uuid}
        className={instituteClass.name}
        canEdit={canEdit}
        saveRef={saveRef}
        onHasChangesChange={setHasChanges}
        onIsSavingChange={setIsSaving}
      />
    </Modal>
  );
}
